import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Typography, BoxBorder } from '@genial/design-tokens';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import {
  TableContentItemCaptions,
  TableContentItemText,
} from '../../../../../../../../../components/TableContent';
import { useFixedIncomeAssetController } from '../../../../../hooks/products/FixedIncome';
import { LateralBar } from '../../../components/LateralBar';
import {
  FeeInfo,
  MaturityInfo,
  NetValueInfo,
  ProfitabilityInfo,
  RiskInfo,
  TaxInfo,
  UnitPriceInfo,
} from '../shared';

import { FixedIncomeItemSelectedTypes } from '.';

export const FixedIncomeItemSelectedDesktop: React.FC<FixedIncomeItemSelectedTypes.Props> = ({
  offer,
  onUpdate,
}) => {
  const stackProps = { w: '1/5' };
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    helpTextProps,
    isFocused,
    showMessageAdjustUnitPrice,
    currentValueFormatted,
    currentValue,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAssetController(offer);

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = {
      ...offer,
      orderValue: currentValue || 0,
      estimatedIncomeCalculated,
      estimatedNetValueCalculated,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.orderFee?.feeId === selectedFee?.feeId || !selectedFee) {
      return;
    }
    const updatedOffer = {
      ...offer,
      orderFee: selectedFee,
      estimatedIncomeCalculated,
      estimatedNetValueCalculated,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFee]);

  const infosProps = {
    offer,
    selectedFee,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  return (
    <UI.List testId="selected-product" height="container">
      <LateralBar
        backgroundColor={Color['product.rf']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack space="stack.xxxs" flex={1}>
        <HStack flex={1} justifyContent="space-between">
          <UI.ListItem testId="sp-Produto" stackProps={stackProps}>
            <VStack maxW="100%" pr="inline.xxxs" space="stack.quarck">
              <TableContentItemText
                variant="semibold"
                color="neutral.black"
                tooltip={offer.productDisplayNormalized}>
                {offer.productDisplayNormalized}
              </TableContentItemText>
              <TableContentItemCaptions icons={offer.captions} />
            </VStack>
          </UI.ListItem>
          <RiskInfo w="1/5" {...infosProps} />
          <TaxInfo w="1/5" {...infosProps} />
          <ProfitabilityInfo w="1/5" {...infosProps} />
          <MaturityInfo w="1/5" {...infosProps} />
        </HStack>
        <HStack flex={1} justifyContent="space-between">
          <Stack w="1/5">
            <VStack space="stack.quarck" justifyContent="space-between">
              <HStack space="inline.quarck" alignItems="center">
                <UI.Text variant="semibold">Taxa</UI.Text>
                <UI.Text fontSize="xs">(Spread)</UI.Text>
              </HStack>
              <Stack pr="inline.xxxs" w="full">
                <Stack bgColor="backgroundshade" borderRadius={BoxBorder.thick} flex={1}>
                  <UI.Select
                    triggerProps={{
                      isDisabled: !ableToChangeFees,
                      _disabled: { opacity: 0.2 },
                    }}
                    testID="fee-select"
                    options={offer.distributionFeesNormalized.map((feeOption) => ({
                      label: feeOption.label,
                      value: feeOption.feeId.toString(),
                    }))}
                    placeholder="--"
                    selectedValue={selectedFeeId?.toString() || ''}
                    onValueChange={(value) => setSelectedFeeId(Number(value))}
                  />
                </Stack>
              </Stack>
            </VStack>
          </Stack>
          <UnitPriceInfo w="1/5" {...infosProps} />
          <FeeInfo w="1/5" {...infosProps} />
          <UI.ListItem testId="sp-Valor" title="Valor a aplicar" stackProps={stackProps}>
            <Stack pr="inline.xxxs" w="full">
              <InputMoney
                id={`fixedIncome-input${id}`}
                name="createAppliedAmount"
                control={control}
                handleFocus={handleInputFocus}
                handleBlur={handleInputBlur}
                handleSubmit={handleSubmit}
                handleClear={handleInputClear}
                currentValue={currentValueFormatted}
                hasFocus={isFocused}
                minValue={minApplication}
                customInputProps={{
                  ...{
                    ...(showMessageAdjustUnitPrice && {
                      helpTextProps,
                      helpText,
                    }),
                  },
                  ...{ ...(isFocused && hasError && { error: hasError }) },
                  ...{ ...(isFocused && !hasError && { helpText }) },
                }}
              />
              {showMessageAdjustUnitPrice && !isFocused && (
                <TableContentItemText fontSize={Typography['fontsize.xs']} color="neutral.light">
                  Ajustado por preço unitário
                </TableContentItemText>
              )}
            </Stack>
          </UI.ListItem>
          <NetValueInfo w="1/5" {...infosProps} />
        </HStack>
      </VStack>
    </UI.List>
  );
};
